/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { makeT } from "app/client/lib/localization";
import { HomeModel } from "app/client/models/HomeModel";
import { basicButtonLink, primaryButtonLink } from "app/client/ui2018/buttons";
import { theme } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { knetBadgeIcon, toneActionButton } from "app/client/ui2018/knetIcons";

import { Computed, dom, DomContents, IDisposableOwner, makeTestId, Observable, styled } from "grainjs";

interface BuildHomeIntroCardsOptions {
  homeModel: HomeModel;
}

const t = makeT("HomeIntroCards");

const testId = makeTestId("test-intro-");

const cssHomeIntroCards = styled("div", `
  margin-top: 4px;
  margin-bottom: 16px;
`);

const cssBuildToday = styled("div", `
  display: flex;
  flex-direction: column;
`);

const cssBuildTodayGrid = styled("div", `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
`);

const cssBuildCard = styled("div", `
  display: flex;
  align-items: center;
  padding: 14px 18px;
  background: ${theme.mainPanelBg};
  border: 2px solid color-mix(in srgb, ${theme.controlBorder} 40%, transparent);
  border-radius: 18px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px -6px rgba(0, 0, 0, 0.05);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  gap: 14px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 14px 32px -8px rgba(0, 0, 0, 0.12);
    border-color: ${theme.accentIcon};
  }
`);

const cssBuildCardTitle = styled("div", `
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`);

const cssBuildCardContent = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
  min-width: 0;
`);

const cssBuildCardHeader = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 2px;
`);

// Used only by the disabled status widget in buildCardStatus — restore with it.
// const cssBuildCardSpinner = styled("span", `
//   display: inline-flex;
//   align-items: center;
//   vertical-align: middle;
//   & > div {
//     width: 12px;
//     height: 12px;
//   }
// `);

const cssServiceStatus = styled("div", `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: 12px;
  background: color-mix(in srgb, ${theme.controlBorder} 25%, transparent);
  color: ${theme.lightText};
  width: fit-content;
`);

// Used only by the disabled status widget / stale-data banner — restore with them.
// const cssServiceDot = styled("div", `
//   width: 7px;
//   height: 7px;
//   border-radius: 50%;
//   flex-shrink: 0;
//   &.online  { background-color: #22C55E; }
//   &.sleeping { background-color: #94A3B8; }
// `);
//
// const cssStaleDataBanner = styled("div", `
//   display: flex;
//   align-items: center;
//   font-size: 11px;
//   color: ${theme.lightText};
//   padding: 4px 8px;
//   margin-bottom: 8px;
//   border-radius: 4px;
//   background: ${theme.hover};
//   width: fit-content;
// `);

const cssBuildCardTeaser = styled("div", `
  font-size: 12px;
  color: ${theme.lightText};
  font-weight: 400;
  line-height: 1.45;
`);

const cssBuildCardAction = styled(primaryButtonLink, `
  padding: 5px 14px;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: -0.01em;
  border-radius: 20px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 2px 6px rgba(0, 0, 0, 0.08);
  text-decoration: none !important;
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 4px 10px rgba(0, 0, 0, 0.12);
    transform: scale(1.02);
  }
  &:active {
    transform: scale(0.98);
  }

  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
    box-shadow: none;
    transform: none;
  }
`);

const cssSectionTitle = styled("div", `
  font-size: 12px;
  font-weight: 600;
  color: ${theme.lightText};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.85;
  margin-bottom: 14px;
  margin-top: 28px;
`);

const cssPresetCard = styled(cssBuildCard, `
  background: linear-gradient(135deg, ${theme.mainPanelBg} 0%, color-mix(in srgb, ${theme.hover} 35%, ${theme.mainPanelBg}) 100%);
  border-left: 4px solid ${theme.accentIcon};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px -6px rgba(0, 0, 0, 0.05);
`);

/**
 * Builds the status widget for a Mission Control card.
 *
 * KNet: the Online/Sleeping dot is disabled — determining it required calling
 * every backend service on each home page load, waking their Knative services
 * from scale-to-zero just to render a status label. Only the item count (from
 * the localStorage cache) is shown now. To restore live status, set
 * SKIP_EXTERNAL_SERVICE_FETCHES to false in HomeModel.ts and un-comment the
 * dot + label rendering below.
 */
function buildCardStatus(
  _loading: Observable<boolean | "slow">,
  _isOnline: Observable<boolean>,
  countObs?: Observable<number> | Computed<number>,
): DomContents {
  if (!countObs) { return null; }
  return dom.domComputed(countObs, n =>
    n > 0 ? cssServiceStatus(dom("span", `${n}`)) : null,
  );
  // Original status widget (needs the Phase-2 fetches in HomeModel enabled):
  // return dom.domComputed(
  //   use => ({ isLoading: use(_loading), count: countObs ? use(countObs) : undefined, online: use(_isOnline) }),
  //   ({ isLoading, count, online }) => {
  //     if (isLoading) {
  //       // Show stale count immediately if we have it; spinner indicates refresh in progress
  //       const hasStaleCount = count !== undefined && count > 0;
  //       return cssServiceStatus(
  //         cssServiceDot(dom.cls("sleeping", true)),
  //         hasStaleCount
  //           ? dom("span", `${count} · `)
  //           : null,
  //         cssBuildCardSpinner(loadingSpinner()),
  //       );
  //     }
  //     const statusLabel = online ? t("Online") : t("Sleeping");
  //     return cssServiceStatus(
  //       cssServiceDot(dom.cls("online", online), dom.cls("sleeping", !online)),
  //       countObs
  //         ? dom.domComputed(countObs, (n) =>
  //             dom("span", n > 0 ? `${n} · ${statusLabel}` : statusLabel)
  //           )
  //         : dom("span", statusLabel),
  //     );
  //   }
  // );
}

// Used only by the disabled stale-data banner — restore with it.
// function _formatStaleTime(iso: string): string {
//   const d = new Date(iso);
//   const now = Date.now();
//   const diffMs = now - d.getTime();
//   const diffMins = Math.floor(diffMs / 60000);
//   if (diffMins < 1) { return "just now"; }
//   if (diffMins < 60) { return `${diffMins}m ago`; }
//   const diffHrs = Math.floor(diffMins / 60);
//   if (diffHrs < 24) { return `${diffHrs}h ago`; }
//   // Older than a day: show date + time
//   return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
//     " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
// }

export function buildHomeIntroCards(
  owner: IDisposableOwner,
  { homeModel }: BuildHomeIntroCardsOptions,
) {
  const agentCount = Computed.create(owner, use => use(homeModel.knetAgents).length);
  const tableCount = Computed.create(owner, (use) => {
    let count = 0;
    for (const ws of use(homeModel.workspaces)) {
      if (!ws.isSupportWorkspace) {
        count += ws.docs.length;
      }
    }
    return count;
  });
  const collectionCount = Computed.create(owner, use => use(homeModel.aperagCollections).length);
  const toolCount = Computed.create(owner, use => use(homeModel.connectAppConfigs).length);
  const gisCount = Computed.create(owner, use => use(homeModel.gisProjects).length);
  const crawlerCount = Computed.create(owner, use => use(homeModel.crawlers).length);

  const isAnonymous = !homeModel.app.currentValidUser;

  return dom.maybe(use => !use(homeModel.onlyShowDocuments), () => cssHomeIntroCards(
    // KNet: "Showing data from … · Refreshing…" banner disabled along with the
    // Phase-2 service fetches (SKIP_EXTERNAL_SERVICE_FETCHES in HomeModel.ts) —
    // nothing refreshes the cache anymore, so the banner would mislead.
    // !isAnonymous ? dom.domComputed(
    //   use => ({ loading: use(homeModel.loading), ts: use(homeModel.lastFetchedAt) }),
    //   ({ loading, ts }) => loading && ts
    //     ? cssStaleDataBanner(
    //         icon("Revert", { style: 'width:12px;height:12px;margin-right:6px;opacity:0.6;' }),
    //         t("Showing data from {{time}} · Refreshing…", { time: _formatStaleTime(ts) }),
    //       )
    //     : null
    // ) : null,
    // Show guest message box for anonymous users
    isAnonymous ? cssGuestMessageBox(
      cssGuestMessageIcon(icon("Home")),
      cssGuestMessageContent(
        cssGuestMessageTitle(t("Welcome to KNetAI")),
        cssGuestMessageText(
          t("Experience KNet AI, a multi-agent orchestration layer. Build resilient Knowledge Networks (K-Nets) " +
            "that combine agents, flows, and human review into stateful, durable business solutions."),
        ),
      ),
      cssGuestMessageActions(
        // TODO: Implement signup functionality
        // cssGuestActionButton(
        //   t("Sign Up"),
        //   { href: "/signup", target: "_self" },
        //   testId("guest-signup-btn"),
        // ),
        cssGuestActionButtonSecondary(
          t("Log In"),
          { href: "/login", target: "_self" },
          testId("guest-login-btn"),
        ),
      ),
      testId("guest-message-box"),
    ) : null,
    cssBuildToday(
      cssSectionTitle(t("K-Net Templates")),
      cssBuildTodayGrid(
        cssPresetCard(
          dom.style("border-left-color", "#4f46e5"),
          knetBadgeIcon("flows"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Data Intelligence Network")),
              cssBuildCardTeaser(t("Resilient orchestration for trend analysis and automated insight generation")),
            ),
            cssBuildCardAction(t("Provision"), { href: "#" }, toneActionButton("#4f46e5")),
          ),
          testId("preset-data-analyst"),
        ),
        cssPresetCard(
          dom.style("border-left-color", "#db2777"),
          knetBadgeIcon("maps"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Spatial Intelligence Network")),
              cssBuildCardTeaser(t("Stateful mapping of geographic patterns and spatial relationships")),
            ),
            cssBuildCardAction(t("Provision"), { href: "#" }, toneActionButton("#db2777")),
          ),
          testId("preset-spatial-researcher"),
        ),
        cssPresetCard(
          dom.style("border-left-color", "#d97706"),
          knetBadgeIcon("knowledgebases"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Documentation Orchestrator")),
              cssBuildCardTeaser(t("Centralize and query organizational knowledge with absolute memory")),
            ),
            cssBuildCardAction(t("Provision"), { href: "#" }, toneActionButton("#d97706")),
          ),
          testId("preset-knowledge-manager"),
        ),
      ),

      cssSectionTitle(t("Resources")),
      cssBuildTodayGrid(
        cssBuildCard(
          knetBadgeIcon("knets"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("K-Nets")),
              isAnonymous ?
                cssBuildCardTeaser(t("Resilient orchestrations for complex business solutions")) :
                buildCardStatus(homeModel.loading, homeModel.knetAgentsOnline, agentCount),
            ),
            cssBuildCardAction(t("Explore"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.attr("target", "_blank"),
                dom.attr("href", (use) => {
                  const workspaces = use(homeModel.workspaces);
                  const wsId = use(homeModel.currentWSId) || (workspaces[0]?.id || 1);
                  const orgId = homeModel.effectiveOrgId;
                  return orgId && wsId ? `/flows/o/${orgId}/ws/${wsId}/knets/` : "/flows/";
                }),
              ],
            ),
          ),
          testId("build-agents"),
        ),
        cssBuildCard(
          knetBadgeIcon("databases"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Databases")),
              isAnonymous ?
                cssBuildCardTeaser(t("Smart spreadsheets to organize and analyze your data")) :
                buildCardStatus(homeModel.loading, homeModel.available, tableCount),
            ),
            cssBuildCardAction(t("Open"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.on("click", (ev) => {
                  ev.preventDefault();
                  homeModel.onlyShowDocuments.set(true);
                }),
                { href: "#" },
              ],
            ),
          ),
          testId("build-tables"),
        ),
        cssBuildCard(
          knetBadgeIcon("knowledgebases"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Knowledge Bases")),
              isAnonymous ?
                cssBuildCardTeaser(t("Knowledge bases for your K-Nets to learn from")) :
                buildCardStatus(homeModel.loading, homeModel.aperagCollectionsOnline, collectionCount),
            ),
            cssBuildCardAction(t("View"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.attr("target", "_blank"),
                dom.attr("href", (use) => {
                  const workspaces = use(homeModel.workspaces);
                  const wsId = use(homeModel.currentWSId) || (workspaces[0]?.id || 1);
                  const orgId = homeModel.effectiveOrgId;
                  return orgId && wsId ? `/knowledgebases/o/${orgId}/ws/${wsId}/collections/` : "/knowledgebases/";
                }),
              ],
            ),
          ),
          testId("build-collections"),
        ),
        cssBuildCard(
          knetBadgeIcon("connectors"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Connectors")),
              isAnonymous ?
                cssBuildCardTeaser(t("Integrations with your favorite applications")) :
                buildCardStatus(homeModel.loading, homeModel.configuredAppsOnline, toolCount),
            ),
            cssBuildCardAction(t("Browse"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.attr("target", "_blank"),
                dom.attr("href", (use) => {
                  const workspaces = use(homeModel.workspaces);
                  const wsId = use(homeModel.currentWSId) || (workspaces[0]?.id || 1);
                  const orgId = homeModel.effectiveOrgId;
                  return orgId && wsId ? `/connectors/o/${orgId}/ws/${wsId}/available` : "/connectors";
                }),
              ],
            ),
          ),
          testId("build-tools"),
        ),
        cssBuildCard(
          knetBadgeIcon("maps"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Maps")),
              isAnonymous ?
                cssBuildCardTeaser(t("Interactive maps and GIS projects")) :
                buildCardStatus(homeModel.loading, homeModel.gisProjectsOnline, gisCount),
            ),
            cssBuildCardAction(t("Open"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.attr("target", "_blank"),
                dom.attr("href", (use) => {
                  const workspaces = use(homeModel.workspaces);
                  const wsId = use(homeModel.currentWSId) || (workspaces[0]?.id || 1);
                  const orgId = homeModel.effectiveOrgId;
                  return orgId && wsId ? `/maps/o/${orgId}/ws/${wsId}/` : "/maps/";
                }),
              ],
            ),
          ),
          testId("build-maps"),
        ),
        cssBuildCard(
          knetBadgeIcon("crawlers"),
          cssBuildCardContent(
            cssBuildCardHeader(
              cssBuildCardTitle(t("Crawlers")),
              isAnonymous ?
                cssBuildCardTeaser(t("Web crawlers to ingest data from external sources")) :
                buildCardStatus(homeModel.loading, homeModel.crawlersOnline, crawlerCount),
            ),
            cssBuildCardAction(t("Explore"),
              isAnonymous ? dom.boolAttr("disabled", true) : [
                dom.attr("target", "_blank"),
                dom.attr("href", (use) => {
                  const workspaces = use(homeModel.workspaces);
                  const wsId = use(homeModel.currentWSId) || (workspaces[0]?.id || 1);
                  const orgId = homeModel.effectiveOrgId;
                  return orgId && wsId ? `/crawlers/o/${orgId}/ws/${wsId}/crawlers/` : "/crawlers/";
                }),
              ],
            ),
          ),
          testId("build-crawlers"),
        ),
        // dom.maybe(getGristConfig().assistant, () =>
        //   cssBuildCard(
        //     cssBuildCardIconBadge({ style: 'background-color: #E8D0FE;' },
        //       cssBuildCardIconInner("Robot", dom.style('background-color', '#6A0DAD')),
        //     ),
        //     cssBuildCardContent(
        //       cssBuildCardHeader(
        //         cssBuildCardTitle(t("Kue")),
        //         isAnonymous ?
        //           cssBuildCardTeaser(t("Your smart document assistant for automation and insights")) :
        //           cssBuildCardStatus(dom("span", t("Smart document assistant"))),
        //       ),
        //       cssBuildCardAction(t("Open"),
        //         isAnonymous ? dom.boolAttr("disabled", true) : [
        //           dom.on("click", (ev) => {
        //             ev.preventDefault();
        //             homeModel.app.openGlobalAssistant();
        //           }),
        //           { href: "#" }
        //         ],
        //       ),
        //     ),
        //     testId("build-assistant"),
        //   )
        // ),
      ),
    ),
  ));
}

// Guest Message Box Styles
const cssGuestMessageBox = styled("div", `
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px 28px;
  margin-bottom: 24px;
  background: ${theme.mainPanelBg};
  border: 2px solid color-mix(in srgb, ${theme.controlBorder} 50%, transparent);
  border-radius: 20px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 12px 32px -8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  
  &:hover {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 16px 40px -8px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`);

const cssGuestMessageIcon = styled("div", `
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.controlPrimaryBg} 0%, ${theme.accentIcon} 100%);
  border-radius: 16px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 4px 12px rgba(0, 0, 0, 0.15);
  
  --icon-color: white;
  
  & > div {
    width: 26px;
    height: 26px;
  }
`);

const cssGuestMessageContent = styled("div", `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`);

const cssGuestMessageTitle = styled("div", `
  font-size: 19px;
  font-weight: 600;
  color: ${theme.text};
  letter-spacing: -0.015em;
`);

const cssGuestMessageText = styled("div", `
  font-size: 13.5px;
  line-height: 1.5;
  color: ${theme.lightText};
  font-weight: 400;
`);

const cssGuestMessageActions = styled("div", `
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`);

const cssGuestActionButtonSecondary = styled(basicButtonLink, `
  padding: 8px 22px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  border-radius: 20px;
  text-decoration: none !important;
  white-space: nowrap;
  border: 1.5px solid color-mix(in srgb, ${theme.controlBorder} 60%, transparent);
  background: color-mix(in srgb, ${theme.hover} 30%, transparent);
  color: ${theme.text};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  
  &:hover {
    background: ${theme.hover};
    transform: scale(1.02);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 4px 10px rgba(0, 0, 0, 0.1);
  }
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`);
